import * as React from 'react';
import {Box, Typography, Tooltip, RadioGroup, FormControlLabel, Radio} from '@mui/material';
import {getOrderStatus, formatDate, getYYYYMMDD, formatNumber, filterShipmentsByFlow} from "../../helpers/UtilityHelper";
import {useGetAllShipmentsWithPickupAndDeliveryDataQuery} from "../../redux/commonRTK";
import {StackedBarChart} from "../charts/ColumnChart";
import {MiniMap, Donut, Histogram} from "./NewCharts";
import OrderSummary from "./orders/OrderSummary";
import CourierBenchmarkScatter from "./CourierBenchmarkScatter";
import _ from 'lodash'
import {differenceInDays, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, getWeek} from "date-fns";
import Loader from '../../common/Loader';
import {useGetCouriersQuery} from '../../redux/commonRTKPersist';
import {getMerchant} from '../../helpers/ReduxHelpers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography';
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, MUTED, STATUS_COLORS} from '../../styles/foundation/colors';
import {SHADOW} from '../../styles/foundation/effects';
import {cockpitTitleSx} from './dashboardStyles';

const cardSx = {
    bgcolor: WHITE, borderRadius: '10px', p: 2.5, mb: 2.5,
    boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
    transition: 'box-shadow 0.2s',
    '&:hover': {boxShadow: SHADOW.cardHover},
};
const cardTitleSx = cockpitTitleSx;
const TRACK = '#f1f4f8';
// Equal-height card (fills its grid row) with a column layout so content can be vertically centred.
// mb:0 — vertical spacing between rows is owned by the CSS-grid wrapper's `gap`, not the card.
const flexCardSx = {...cardSx, mb: 0, height: '100%', display: 'flex', flexDirection: 'column'};
// Two-up row: real CSS-grid gap (no MUI negative-margin cancellation), equal-width columns.
const twoUpRowSx = {display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2.5, mb: 2.5};
// One colour per zone slice in the Zone & Shipments donut.
const ZONE_PALETTE = ['#3783e7', '#2e9e6b', '#d99a3a', '#c0392b', '#7a4fb0', '#0f766e', '#8f2f2c'];

// Cockpit ranked bar list — name · proportional bar · count (· optional %).
function RankedList({rows}) {
    const max = Math.max(...rows.map((r) => r.value), 1);
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.1}}>
            {rows.map((r) => (
                <Box key={r.name} sx={{display: 'grid', gridTemplateColumns: '104px 1fr 64px', gap: 1, alignItems: 'center'}}>
                    <Typography sx={{fontFamily: FONT, fontSize: '11.5px', color: SUBTLE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{r.name}</Typography>
                    <Tooltip arrow title={`${r.name}: ${r.value.toLocaleString()} shipment${r.value === 1 ? '' : 's'}`}>
                        <Box sx={{height: 8, bgcolor: TRACK, borderRadius: '5px', overflow: 'hidden', cursor: 'default'}}>
                            <Box sx={{height: '100%', width: `${(r.value / max) * 100}%`, bgcolor: r.color || PRIMARY_BLUE, borderRadius: '5px'}} />
                        </Box>
                    </Tooltip>
                    <Typography sx={{fontFamily: FONT, fontSize: '11.5px', fontWeight: WEIGHT.bold, color: NAVY, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                        {r.value.toLocaleString()}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// Cockpit payment donut with COD % in the center.
function PaymentDonut({cod, prepaid}) {
    const total = cod + prepaid || 1;
    const codPct = (cod / total) * 100;
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, justifyContent: 'center', py: 1, flexWrap: 'wrap'}}>
            <Box sx={{
                width: 150, height: 150, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', position: 'relative',
                background: `conic-gradient(${ERROR} 0 ${codPct}%, ${SUCCESS} ${codPct}% 100%)`,
                '&::after': {content: '""', position: 'absolute', width: 96, height: 96, bgcolor: WHITE, borderRadius: '50%'},
            }}>
                <Box sx={{position: 'relative', zIndex: 1, textAlign: 'center'}}>
                    <Typography sx={{fontFamily: FONT, fontSize: '24px', fontWeight: WEIGHT.extra, color: ERROR, lineHeight: 1}}>{codPct.toFixed(0)}%</Typography>
                    <Typography sx={{fontFamily: FONT, fontSize: '10px', color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.05em'}}>COD</Typography>
                </Box>
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                {[{l: 'COD', v: cod, c: ERROR}, {l: 'Prepaid', v: prepaid, c: SUCCESS}].map((x) => {
                    const pct = Math.round((x.v / total) * 100);
                    return (
                    <Tooltip key={x.l} arrow title={`${x.l}: ${x.v.toLocaleString()} (${pct}% of ${total.toLocaleString()})`}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'default'}}>
                            <Box sx={{width: 10, height: 10, borderRadius: '3px', bgcolor: x.c}} />
                            <Typography sx={{fontFamily: FONT, fontSize: '12px', color: SUBTLE}}>
                                {x.l} · <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.bold}}>{x.v.toLocaleString()}</Box> <Box component='span' sx={{color: SUBTLE}}>({pct}%)</Box>
                            </Typography>
                        </Box>
                    </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
}

export default function Orders({dateRange, shipType, flow = 'ALL'}) {
    const {fromDate: from_date, toDate: to_date} = {fromDate: dateRange[0].startDate, toDate: dateRange[0].endDate}
    const requestPayload = {from_date: getYYYYMMDD(from_date), to_date: getYYYYMMDD(to_date), merchant_list: getMerchant().id, shipmentType_list: shipType};
    const skip = !from_date || !to_date;
    const {data: allShipments, isLoading, isFetching} = useGetAllShipmentsWithPickupAndDeliveryDataQuery(requestPayload, {skip});

    if (isLoading || isFetching) return <Loader />

    // Scope to the active flow (Forward / Reverse) before computing any metric.
    const displayData = filterShipmentsByFlow(Array.isArray(allShipments) ? allShipments : [], flow);
    if (!displayData.length) {
        return (
            <Box sx={{...cardSx, textAlign: 'center', py: 6}}>
                <InfoOutlinedIcon sx={{fontSize: 40, color: BORDER, mb: 1}} />
                <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>No shipments in this period</Typography>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5}}>
                    No {shipType === 'B2B' ? 'PTL' : 'D2C'} shipments were booked in the selected date range. Try a wider range.
                </Typography>
            </Box>
        )
    }

    // Weight distribution
    let weightChartData = {
        half: {label: '0 – 500 g', value: 0}, one: {label: '500 g – 1 kg', value: 0},
        two: {label: '1 – 2 kg', value: 0}, three: {label: '2 – 3 kg', value: 0},
        four: {label: '3 – 4 kg', value: 0}, five: {label: '4 – 5 kg', value: 0},
        ten: {label: '5 – 10 kg', value: 0}, twenty: {label: '10 – 20 kg', value: 0},
        fourty: {label: '20 – 40 kg', value: 0}, more: {label: '40 kg +', value: 0}
    }
    const orderItemWeight = _(displayData || []).groupBy((s) => (s.weight && s.weight[0]) ?? s.shipment_weight).value()
    for (const key in orderItemWeight) {
        if (key <= 500) weightChartData.half.value += orderItemWeight[key].length;
        else if (key <= 1000) weightChartData.one.value += orderItemWeight[key].length;
        else if (key <= 2000) weightChartData.two.value += orderItemWeight[key].length;
        else if (key <= 3000) weightChartData.three.value += orderItemWeight[key].length;
        else if (key <= 4000) weightChartData.four.value += orderItemWeight[key].length;
        else if (key <= 5000) weightChartData.five.value += orderItemWeight[key].length;
        else if (key <= 10000) weightChartData.ten.value += orderItemWeight[key].length;
        else if (key <= 20000) weightChartData.twenty.value += orderItemWeight[key].length;
        else if (key <= 40000) weightChartData.fourty.value += orderItemWeight[key].length;
        else weightChartData.more.value += orderItemWeight[key].length;
    }
    if (weightChartData.fourty.value === 0) delete weightChartData.fourty
    if (weightChartData.more.value === 0) delete weightChartData.more
    weightChartData = Object.values(weightChartData)

    const orderPrepayVSCODInfo = _(displayData || []).groupBy('paymentMode').map((item, key) => ({name: key, value: item.length})).value()
    const isReverse = flow === 'REVERSE';
    const spreadField = isReverse ? 'pickupState' : 'deliveryState';
    const orderDistribInfo = _(displayData || []).groupBy(spreadField).transform((result, items, key) => {
        const totalOrders = (displayData || []).length
        result[key] = {value: items.length, per: ((items.length / totalOrders) * 100).toFixed(2) + "%"}
    }).value()

    // Ranked top states (top 6 + Other) for the cockpit Delivery/Pickup Spread list.
    const stateArr = Object.keys(orderDistribInfo)
        .map((k) => ({name: (k && k !== 'undefined') ? k : 'Unknown', value: orderDistribInfo[k].value}))
        .sort((a, b) => b.value - a.value)
    const stateRows = stateArr.slice(0, 6).map((r) => ({...r, color: PRIMARY_BLUE}))
    const stateOther = stateArr.slice(6).reduce((s, r) => s + r.value, 0)
    if (stateOther > 0) stateRows.push({name: `Other · ${stateArr.length - 6}`, value: stateOther, color: MUTED})

    // Ranked zones for the Zone & Shipments breakup.
    const zoneGrp = _.groupBy(displayData, 'merchantZone')
    const zoneRows = Object.keys(zoneGrp)
        .map((z) => ({name: (z && z !== 'undefined') ? z : 'Unknown', value: zoneGrp[z].length}))
        .sort((a, b) => b.value - a.value)
        .map((r, i) => ({...r, color: ZONE_PALETTE[i % ZONE_PALETTE.length]}))

    // Payment split (COD vs everything else = prepaid).
    const codCount = _.sumBy((orderPrepayVSCODInfo || []).filter((p) => /cod/i.test(p.name)), 'value')
    const totalPay = _.sumBy(orderPrepayVSCODInfo || [], 'value')
    const prepaidCount = totalPay - codCount

    return (
        <>
            <OrderSummary data={displayData} flow={flow} />

            <RecentShipments data={displayData} from_date={from_date} to_date={to_date} flow={flow} />

            <Box sx={twoUpRowSx}>
                <Box sx={flexCardSx}>
                    <Typography sx={{...cardTitleSx, mb: 1, pb: 1, borderBottom: 'none'}}>{isReverse ? 'Pickup Spread' : 'Delivery Spread'}</Typography>
                    <Typography sx={{fontFamily: FONT, fontSize: '11px', color: SUBTLE, mb: 1.75, pb: 1.5, borderBottom: `1px solid ${BORDER}`}}>
                        {isReverse ? 'Which states your pickups are coming from (most to least).' : 'Which states your orders are going to (most to least).'}
                    </Typography>
                    <Box sx={{flex: 1, display: 'grid', gridTemplateColumns: '104px 1fr', gap: 2, alignItems: 'center'}}>
                        <MiniMap />
                        <RankedList rows={stateRows} />
                    </Box>
                </Box>
                <Box sx={flexCardSx}>
                    <Typography sx={cardTitleSx}>Zone &amp; Shipments Breakup</Typography>
                    <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <Donut data={zoneRows} centerValue={displayData.length.toLocaleString()} centerLabel="Shipments" />
                    </Box>
                </Box>
            </Box>

            <Box sx={twoUpRowSx}>
                {!isReverse && (
                    <Box sx={flexCardSx}>
                        <Typography sx={cardTitleSx}>Prepaid vs COD</Typography>
                        <Box sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <PaymentDonut cod={codCount} prepaid={prepaidCount} />
                        </Box>
                    </Box>
                )}
                <Box sx={flexCardSx}>
                    <Typography sx={{...cardTitleSx, mb: 1, pb: 1, borderBottom: 'none'}}>Weight Profile</Typography>
                    <Typography sx={{fontFamily: FONT, fontSize: '11px', color: SUBTLE, mb: 1.75, pb: 1.5, borderBottom: `1px solid ${BORDER}`}}>
                        How heavy your parcels are — heavier parcels usually cost more to ship.
                    </Typography>
                    <Box sx={{flex: 1, display: 'flex', alignItems: 'center'}}>
                        <Box sx={{width: '100%'}}><Histogram data={weightChartData} height={220} /></Box>
                    </Box>
                </Box>
            </Box>

            {/* Courier benchmarking — Delivery % vs RTO %, bubble = volume */}
            <CourierBenchmarkScatter data={displayData} />
        </>
    )
}

function RecentShipments({data = [], from_date, to_date, flow = 'ALL'}) {
    const {data: allCouriers} = useGetCouriersQuery()
    const [groupBy, setGroupBy] = React.useState("status")

    const startDate = new Date(from_date)
    const endDate = new Date(to_date)
    let allData = groupBy === "courier"
        ? (data || []).map((order) => {
            const courier = allCouriers?.find(c => c.id === order.courier)
            return {...order, courier: courier?.parent || 'N/A'}
        })
        : data || []

    allData = allData.map((row) => ({...row, week: 'Week ' + getWeek(new Date(row.awbRegisteredDate))}))

    const ordersGroupedByStatus = []
    const dateDiff = differenceInDays(endDate, startDate)
    let allDates = [], allGroupedData = [], type = 'days'

    // Bucket the x-axis so the chart never turns up empty at any range, matching the
    // original production behaviour: < 32 days → daily · < 180 days → weekly · beyond → monthly
    // (monthly is the only addition — the old chart drew nothing past 180 days).
    if (dateDiff < 32) {
        allDates = eachDayOfInterval({start: startDate, end: endDate}).map(row => formatDate(row, 'yyyy-MM-dd'))
        allGroupedData = _.groupBy(allData, (item) => getYYYYMMDD(new Date(item.awbRegisteredDate)))
    } else if (dateDiff < 180) {
        allDates = eachWeekOfInterval({start: startDate, end: endDate}).map(row => 'Week ' + getWeek(row))
        allGroupedData = _.groupBy(allData, 'week')
        type = 'week'
    } else {
        allDates = eachMonthOfInterval({start: startDate, end: endDate}).map(row => formatDate(row, 'MMM yyyy'))
        allGroupedData = _.groupBy(allData, (item) => formatDate(new Date(item.awbRegisteredDate), 'MMM yyyy'))
        type = 'month'
    }

    for (const k of allDates) {
        const obj = {name: type === 'days' ? formatDate(k, 'dd-MMM') : k}
        const dataOnDate = allGroupedData[k] || []
        for (const shipment of dataOnDate) {
            if (groupBy === 'pickup') {
                const kk = shipment.pickupPincode
                obj[kk] = (obj[kk] || 0) + 1
            } else if (groupBy === 'courier') {
                obj[shipment.courier] = (obj[shipment.courier] || 0) + 1
            } else {
                const orderStatus = getOrderStatus(shipment.orderStatus)
                obj[orderStatus] = (obj[orderStatus] || 0) + 1
            }
        }
        obj.__total = Object.keys(obj).reduce((s, kk) => (kk === 'name' ? s : s + (Number(obj[kk]) || 0)), 0)
        ordersGroupedByStatus.push(obj)
    }

    // Fixed semantic status palette — same colour means the same status on every chart.
    let chartColumns = [
        {label: 'Pending Pickup', dataKey: 'ORDER_PLACED', fill: STATUS_COLORS.ORDER_PLACED},
        {label: 'In Transit', dataKey: 'INTRANSIT', fill: STATUS_COLORS.INTRANSIT},
        {label: 'NDR / Failed', dataKey: 'DELIVERY_FAILED', fill: STATUS_COLORS.DELIVERY_FAILED},
        {label: 'Delivered', dataKey: 'DELIVERED', fill: STATUS_COLORS.DELIVERED},
        {label: 'RTO', dataKey: 'RTO', fill: STATUS_COLORS.RTO},
        {label: 'Lost', dataKey: 'LOST', fill: STATUS_COLORS.LOST},
        {label: 'Cancelled', dataKey: 'CANCELLED', fill: STATUS_COLORS.CANCELLED},
    ]
    if (groupBy === 'courier' || groupBy === 'pickup') {
        const d = _.uniq(_.flatten(ordersGroupedByStatus.map(r => Object.keys(r))))
        chartColumns = d.filter(r => r !== 'name' && r !== '__total').map(r => ({label: r, dataKey: r}))
    }

    // Period summary metrics — computed from the full shipment list (independent of the
    // By Status / Courier / Pickup toggle, which only changes how the chart is split).
    const statusCount = _.countBy(data || [], (s) => getOrderStatus(s.orderStatus))
    const totalShip = (data || []).length
    const pctOf = (v) => (totalShip ? Math.round((v / totalShip) * 100) : 0)
    const metrics = [
        {label: 'Total Shipments', value: totalShip, color: NAVY},
        {label: 'Delivered', value: statusCount.DELIVERED || 0, sub: `${pctOf(statusCount.DELIVERED || 0)}%`, color: STATUS_COLORS.DELIVERED},
        {label: 'In Transit', value: statusCount.INTRANSIT || 0, sub: `${pctOf(statusCount.INTRANSIT || 0)}%`, color: STATUS_COLORS.INTRANSIT},
        {label: 'Pending Pickup', value: statusCount.ORDER_PLACED || 0, sub: `${pctOf(statusCount.ORDER_PLACED || 0)}%`, color: STATUS_COLORS.ORDER_PLACED},
        ...(flow !== 'REVERSE' ? [{label: 'RTO', value: statusCount.RTO || 0, sub: `${pctOf(statusCount.RTO || 0)}%`, color: STATUS_COLORS.RTO}] : []),
    ]

    return (
        <Box sx={cardSx}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 2}}>
                <Typography sx={{...cardTitleSx, mb: 0, pb: 0, borderBottom: 'none'}}>Shipment Analytics</Typography>
                <RadioGroup row onChange={(e) => setGroupBy(e.target.value)} value={groupBy}>
                    <FormControlLabel value="status" control={<Radio size="small" />}
                        label={<Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}>By Status</Typography>} />
                    <FormControlLabel value="courier" control={<Radio size="small" />}
                        label={<Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}>By Courier</Typography>} />
                    {flow !== 'REVERSE' && (
                        <FormControlLabel value="pickup" control={<Radio size="small" />}
                            label={<Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}>By Pickup</Typography>} />
                    )}
                </RadioGroup>
            </Box>
            {/* period summary metrics */}
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2}}>
                {metrics.map((m) => (
                    <Box key={m.label} sx={{flex: '1 1 auto', minWidth: 116, px: 1.5, py: 1, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: WHITE}}>
                        <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.6}}>
                            <Typography sx={{fontFamily: FONT, fontSize: '18px', fontWeight: WEIGHT.extra, lineHeight: 1, color: m.color}}>{formatNumber(m.value)}</Typography>
                            {m.sub && <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.bold, color: SUBTLE}}>{m.sub}</Typography>}
                        </Box>
                        <Typography sx={{fontFamily: FONT, fontSize: '10.5px', color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.4}}>{m.label}</Typography>
                    </Box>
                ))}
            </Box>
            <StackedBarChart width='100%' height={400} chartOptions={{
                xaxis: 'name', stackId: 'status', labelY: 'Shipments', xLabel: 'Date',
                totalKey: '__total', showLabels: true,
                labelMin: Math.max(...ordersGroupedByStatus.map((r) => r.__total || 0), 1) * 0.05,
                data: chartColumns
            }} data={ordersGroupedByStatus} />
        </Box>
    )
}
